import { useState } from "react";
import { FileSpreadsheet, Package, TrendingUp, Wallet, Landmark, ReceiptText, CalendarClock } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { downloadApi } from "../../lib/download";

export default function AdminReports() {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState("");

  const download = async (url, filename, key) => {
    setBusy(key);
    try {
      await downloadApi(url, filename);
      toast.success(`${filename} indirildi`);
    } catch {
      toast.error("Rapor oluşturulamadı");
    } finally {
      setBusy("");
    }
  };

  const qs = `${start ? `start=${start}` : ""}${start && end ? "&" : ""}${end ? `end=${end}` : ""}`;
  const inputCls = "h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

  return (
    <div data-testid="admin-reports-page" className="max-w-5xl">
      <h1 className="font-serif text-2xl sm:text-3xl text-forest">Raporlar</h1>
      <p className="text-stone-500 text-sm mt-1">Satış, stok, cari, banka, fatura ve ödeme planı raporlarını Excel formatında indirin.</p>

      <section className="bg-white rounded-2xl border border-stone-200/80 p-6 mt-6 shadow-sm">
        <h3 className="font-semibold text-stone-900">Tarih Aralığı (satış raporu için)</h3>
        <div className="flex gap-3 mt-3 flex-wrap">
          <input data-testid="report-start-date" type="date" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} />
          <input data-testid="report-end-date" type="date" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
        <p className="text-xs text-stone-400 mt-2">Boş bırakılırsa tüm siparişler dahil edilir.</p>
      </section>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <TrendingUp className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Satış Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">Sipariş bazında ciro, KDV, indirim, kargo ve ödeme durumu + özet sayfası.</p>
          <button data-testid="download-sales-report" onClick={() => download(`/admin/reports/sales.xlsx${qs ? `?${qs}` : ""}`, "satis-raporu.xlsx", "sales")} disabled={busy === "sales"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "sales" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <Package className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Stok Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">SKU, barkod, stok durumu (kritik/stoksuz), maliyet ve stok değeri.</p>
          <button data-testid="download-stock-report" onClick={() => download("/admin/reports/stock.xlsx", "stok-raporu.xlsx", "stock")} disabled={busy === "stock"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "stock" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <Wallet className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Cari Bakiye Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">Tüm cari kartların borç/alacak bakiyeleri ve durumları.</p>
          <button data-testid="download-cari-report" onClick={() => download("/admin/reports/cari.xlsx", "cari-bakiye-raporu.xlsx", "cari")} disabled={busy === "cari"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "cari" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <Landmark className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Banka Hareket Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">Tarih aralıklı tahsilat/ödeme işlemleri, komisyon ve net tutarlar.</p>
          <button data-testid="download-bank-report" onClick={() => download(`/admin/reports/banka.xlsx${qs ? `?${qs}` : ""}`, "banka-hareket-raporu.xlsx", "bank")} disabled={busy === "bank"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "bank" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <ReceiptText className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Fatura Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">Tarih aralıklı faturalar: tutar, KDV, onay ve entegratör durumu.</p>
          <button data-testid="download-invoice-report" onClick={() => download(`/admin/reports/faturalar.xlsx${qs ? `?${qs}` : ""}`, "fatura-raporu.xlsx", "inv")} disabled={busy === "inv"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "inv" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
        <div className="bg-white rounded-2xl border border-stone-200/80 p-6 shadow-sm">
          <CalendarClock className="w-6 h-6 text-forest" />
          <h3 className="font-semibold text-stone-900 mt-3">Ödeme Planı Raporu</h3>
          <p className="text-xs text-stone-500 mt-1">Taksit bazında vadeler, ödeme durumu ve kanalı.</p>
          <button data-testid="download-plan-report" onClick={() => download("/admin/reports/odeme-planlari.xlsx", "odeme-planlari-raporu.xlsx", "plan")} disabled={busy === "plan"} className="mt-4 flex items-center gap-2 bg-forest hover:bg-forest-dark disabled:bg-stone-400 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
            <FileSpreadsheet className="w-4 h-4" /> {busy === "plan" ? "Hazırlanıyor..." : "Excel İndir"}
          </button>
        </div>
      </div>
    </div>
  );
}
